import { useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  Button,
  TextField,
  Alert,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../services/supabase';
import { useData } from '../../contexts/DataContext';
import { extractErrorMessageFromResponse } from '../../services/errorService';

type UserRole = 'SUPER_ADMIN' | 'COMPANY_ADMIN' | 'COMPANY_USER';

interface UserInviteFormProps {
  onInvite: (userData: any) => Promise<void>;
  isSystemLevel: boolean;
  companyId?: string;
}

export const UserInviteForm: React.FC<UserInviteFormProps> = ({
  isSystemLevel,
  companyId,
}: UserInviteFormProps) => {
  const { companies, targetCompany } = useData();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<string>(companyId || targetCompany || '');
  const [role, setRole] = useState<UserRole>('COMPANY_USER');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const { error: inviteError } = await supabase.functions.invoke('send-invite', {
        body: {
          email,
          firstName,
          lastName,
          isSuperAdmin: role === 'SUPER_ADMIN',
          isAdmin: role === 'COMPANY_ADMIN',
          companyId: role !== 'SUPER_ADMIN' ? selectedCompany : undefined,
        },
      });

      if (inviteError) throw inviteError;

      setSuccess(`Invitation sent to ${email}`);
      setEmail('');
      setFirstName('');
      setLastName('');
      setRole('COMPANY_USER');
    } catch (err: unknown) {
      Sentry.captureException(err);
      const errorMessage = await extractErrorMessageFromResponse(err, t);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 400 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TextField
        fullWidth
        label="First Name"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        required
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Last Name"
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        required
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Email Address"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        sx={{ mb: 2 }}
      />

      {isSystemLevel && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Role</InputLabel>
          <Select value={role} label="Role" onChange={(e) => setRole(e.target.value as UserRole)}>
            <MenuItem value="SUPER_ADMIN">Super Admin</MenuItem>
            <MenuItem value="COMPANY_ADMIN">Company Admin</MenuItem>
            <MenuItem value="COMPANY_USER">Company User</MenuItem>
          </Select>
        </FormControl>
      )}

      {!isSystemLevel && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Role</InputLabel>
          <Select value={role} label="Role" onChange={(e) => setRole(e.target.value as UserRole)}>
            <MenuItem value="COMPANY_ADMIN">Company Admin</MenuItem>
            <MenuItem value="COMPANY_USER">Company User</MenuItem>
          </Select>
        </FormControl>
      )}

      {role !== 'SUPER_ADMIN' && !companyId && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Company</InputLabel>
          <Select
            value={selectedCompany}
            label="Company"
            onChange={(e) => setSelectedCompany(e.target.value)}
            disabled={!!companyId}
            required
          >
            {companies.map((company) => (
              <MenuItem key={company.id} value={company.id}>
                {company.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Button type="submit" variant="contained" disabled={loading} fullWidth>
        {loading ? <CircularProgress size={24} /> : 'Send Invitation'}
      </Button>
    </Box>
  );
};
